import React, { useState, useEffect, useRef } from "react";
import { 
  Box, Card, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, IconButton, Icon, Dialog, 
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem, 
  Grid, Tooltip, InputAdornment, Chip, Tab, Tabs, Typography
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import { get, postUpload, getFile, postNoResponse } from "utils/WebService";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";

const TIPOS_DOCUMENTO = [
    { id: 1, label: "Pesquisa Receita Federal", category: "pesquisa" },
    { id: 2, label: "Pesquisa Web", category: "pesquisa" },
    { id: 3, label: "Pesquisa Unitfour", category: "pesquisa" },
    { id: 4, label: "Pesquisa API", category: "pesquisa" },
    { id: 5, label: "Contrato / Termo", category: "contrato" },
    { id: 6, label: "Comprovante / Recibo", category: "comprovante" },
    { id: 7, label: "Procuração", category: "documento" },
    { id: 8, label: "Anexo de Ocorrência", category: "ocorrencia" },
    { id: 99, label: "Outros", category: "outros" }
];

const toCurrency = (val) => {
    if (!val && val !== 0) return "R$ 0,00";
    return Number(val).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
};

const MODELOS_AUTOMATICOS = [
    { id: "relacao_debitos", nome: "Relação dos Débitos", descricao: "Extrato detalhado por contrato com correção, juros, multa e total atualizado", tipo: "pdf" },
    { id: "dossie", nome: "Dossiê Completo em PDF", descricao: "Relatório consolidado da ficha e pesquisas", tipo: "pdf", endpoint: "/dossie/download/" },
    { id: "procuracao", nome: "Procuração Ad Judicia", descricao: "Modelo padrão para representação jurídica", tipo: "docx", fileUrl: "#" },
    { id: "declaracao", nome: "Declaração de Residência", descricao: "Modelo preenchível de comprovação de endereço", tipo: "docx", fileUrl: "#" },
    { id: "termo_acordo", nome: "Termo de Acordo Formalizado", descricao: "Minuta de negociação e compromisso de pagamento", tipo: "docx", fileUrl: "#" },
    { id: "notificacao", nome: "Notificação Extrajudicial", descricao: "Modelo para envio de notificação formal", tipo: "docx", fileUrl: "#" }
];

export default function Documentos({ recordId, isViewOnly }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const { userPerfils } = useUser();
    
    const fileInputRef = useRef(null);
    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [subTab, setSubTab] = useState(0); // 0: Documentos da ficha, 1: Modelos disponíveis
    
    // Relação dos Débitos State
    const [relacaoModalOpen, setRelacaoModalOpen] = useState(false);
    const [relacaoData, setRelacaoData] = useState(null);
    const [loadingRelacao, setLoadingRelacao] = useState(false);
    const [dataSimulacaoInput, setDataSimulacaoInput] = useState(new Date().toISOString().substring(0, 10));
    const [rawDevedorState, setRawDevedorState] = useState(null);
    const [rawContratosState, setRawContratosState] = useState([]);
    // Upload State
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedTipo, setSelectedTipo] = useState(2);
    const [observacao, setObservacao] = useState("");
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [openUploadModal, setOpenUploadModal] = useState(false);

    // Filters State
    const [searchTerm, setSearchTerm] = useState("");
    const [filterType, setFilterType] = useState("TODOS");

    const fetchSimulacaoRelacao = (targetDateStr) => {
        if (!recordId) return;
        setLoadingRelacao(true);

        const dateParts = targetDateStr ? targetDateStr.split("-") : [];
        let displayDate = new Date().toLocaleDateString("pt-BR");
        if (dateParts.length === 3) {
            displayDate = `${dateParts[2].padStart(2, '0')}/${dateParts[1].padStart(2, '0')}/${dateParts[0]}`;
        }

        Promise.all([
            get(`/devedor/by-id/${recordId}`).catch(() => null),
            get(`/contratos/list/${recordId}`).catch(() => null),
            get(`/campanha/simular-calculo-devedor/${recordId}?dataBase=${targetDateStr}`).catch(() => null)
        ]).then(([devedorRes, contratosRes, simulacaoRes]) => {
            const devId = devedorRes?.unencryptedId || devedorRes?.id || recordId || "10274135";
            const devDoc = devedorRes?.documento || devedorRes?.cpfCnpj || devedorRes?.ccDocumento || devedorRes?.cnpj || devedorRes?.cpf || devId;
            const devNome = devedorRes?.nome || devedorRes?.ccNome || devedorRes?.razaoSocial || "CLIENTE DEVEDOR";

            let contratosList = [];

            if (simulacaoRes && Array.isArray(simulacaoRes.contratosCalculadosList) && simulacaoRes.contratosCalculadosList.length > 0) {
                contratosList = simulacaoRes.contratosCalculadosList.map((c) => {
                    let nf = c.titulo || c.numeroContrato || c.numero;
                    if (!nf || (typeof nf === 'string' && nf.length > 25)) {
                        nf = `${devId}-UN`;
                    }
                    let venc = c.dataVencimento || c.vencimento;
                    let vencFormatted = venc ? (typeof venc === 'string' && venc.includes('-') ? venc.split('-').reverse().join('/') : new Date(venc).toLocaleDateString('pt-BR')) : displayDate;
                    
                    const updatedBalance = c.totalAtualizado || (c.valorOriginal + (c.valorCorrecao || 0) + (c.valorMulta || 0) + (c.valorJuros || 0));

                    return {
                        notaFiscal: nf,
                        saldo: updatedBalance || c.valorOriginal || 0,
                        vencimento: vencFormatted
                    };
                });
            } else if (Array.isArray(contratosRes) && contratosRes.length > 0) {
                contratosList = contratosRes.map((c) => {
                    let nf = c.numeroContrato || c.notaFiscal || c.numero || c.titulo || c.codigo;
                    if (!nf || (typeof nf === 'string' && nf.length > 25)) {
                        nf = `${devId}-UN`;
                    }
                    let venc = c.dataVencimento || c.vencimento;
                    let vencFormatted = venc ? (typeof venc === 'string' && venc.includes('-') ? venc.split('-').reverse().join('/') : new Date(venc).toLocaleDateString('pt-BR')) : displayDate;
                    
                    const orig = c.saldo || c.valorOriginal || c.valor || 0;
                    const vencObj = venc ? new Date(venc) : new Date(2022, 4, 10);
                    const targetDateObj = dateParts.length === 3 ? new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]), 12) : new Date();
                    const diffMs = targetDateObj.getTime() - vencObj.getTime();
                    const atrasoDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

                    const correcao = (orig * 0.0005 * atrasoDays);
                    const multa = atrasoDays > 0 ? orig * 0.02 : 0;
                    const juros = orig * (0.01 * (atrasoDays / 30));
                    const updatedSaldo = orig + correcao + multa + juros;

                    return {
                        notaFiscal: nf,
                        saldo: updatedSaldo || orig,
                        vencimento: vencFormatted
                    };
                });
            } else {
                // Default structure recalculating updated balance for targetDateStr
                const orig = devedorRes?.valorSaldo || devedorRes?.valorOriginal || 2238.71;
                const vencObj = devedorRes?.dataVencimento ? new Date(devedorRes.dataVencimento) : new Date(2022, 4, 10);
                const targetDateObj = dateParts.length === 3 ? new Date(Number(dateParts[0]), Number(dateParts[1]) - 1, Number(dateParts[2]), 12) : new Date();
                const diffMs = targetDateObj.getTime() - vencObj.getTime();
                const atrasoDays = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));

                const correcao = (orig * 0.0005 * atrasoDays);
                const multa = atrasoDays > 0 ? orig * 0.02 : 0;
                const juros = orig * (0.01 * (atrasoDays / 30));
                const updatedSaldo = orig + correcao + multa + juros;

                let vencStr = devedorRes?.dataVencimento ? new Date(devedorRes.dataVencimento).toLocaleDateString('pt-BR') : "30/10/2025";
                contratosList = [
                    {
                        notaFiscal: "740505-UN",
                        saldo: updatedSaldo,
                        vencimento: vencStr
                    }
                ];
            }

            const totalGeral = contratosList.reduce((acc, curr) => acc + (curr.saldo || 0), 0);

            setRelacaoData({
                documento: devDoc,
                cliente: devNome,
                devedorId: devId,
                dataSimulacao: displayDate,
                contratos: contratosList,
                totalAtualizado: totalGeral
            });
        }).finally(() => setLoadingRelacao(false));
    };

    const handleOpenRelacaoDebitos = () => {
        if (!recordId) return;
        setRelacaoModalOpen(true);
        const todayIso = new Date().toISOString().substring(0, 10);
        setDataSimulacaoInput(todayIso);
        fetchSimulacaoRelacao(todayIso);
    };

    const handlePrintRelacao = () => {
        if (!relacaoData) return;
        
        const printWindow = window.open('', '', 'width=900,height=750');
        if (!printWindow) {
            window.print();
            return;
        }

        const rowsHtml = relacaoData.contratos.map((c, idx) => {
            if (idx === 0) {
                return `
                    <tr>
                        <td rowspan="${relacaoData.contratos.length}" style="padding: 8px 12px; border: 1px solid #000; text-align: left; vertical-align: top; font-weight: 500;">${relacaoData.documento}</td>
                        <td rowspan="${relacaoData.contratos.length}" style="padding: 8px 12px; border: 1px solid #000; text-align: left; vertical-align: top; font-weight: 500;">${relacaoData.cliente}</td>
                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${c.notaFiscal}</td>
                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${toCurrency(c.saldo)}</td>
                        <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${c.vencimento}</td>
                    </tr>
                `;
            }
            return `
                <tr>
                    <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${c.notaFiscal}</td>
                    <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${toCurrency(c.saldo)}</td>
                    <td style="padding: 8px 12px; border: 1px solid #000; text-align: left;">${c.vencimento}</td>
                </tr>
            `;
        }).join('');

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8" />
                <title></title>
                <style>
                    @page { size: auto; margin: 0; }
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                    html, body { background: #fff !important; margin: 0 !important; padding: 15mm 20mm !important; box-sizing: border-box; }
                    body { font-family: 'Roboto', 'Calibri', 'Segoe UI', Helvetica, Arial, sans-serif; color: #000; }
                    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 13px; border: 1px solid #000; }
                    th { background-color: #b8860b !important; color: #000 !important; font-weight: bold; padding: 8px 12px; border: 1px solid #000; text-align: left; font-size: 13px; }
                    td { border: 1px solid #000; padding: 8px 12px; text-align: left; }
                </style>
            </head>
            <body>
                <table>
                    <thead>
                        <tr>
                            <th>Documento</th>
                            <th>Cliente</th>
                            <th>Nota fiscal</th>
                            <th>Saldo</th>
                            <th>Vencimento</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${rowsHtml}
                    </tbody>
                </table>

                <script>
                    window.onload = function() {
                        setTimeout(function() {
                            window.print();
                            window.close();
                        }, 250);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    const loadDocumentos = () => {
        if (!recordId) return;
        setLoading(true);
        get(`/dossie/list/${recordId}`)
            .then(res => {
                if (Array.isArray(res)) setDocumentos(res);
            })
            .catch(err => console.error("Erro ao carregar documentos", err))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (recordId) {
            loadDocumentos();
            const handleUpdate = () => loadDocumentos();
            window.addEventListener("devedorDocumentoUpdated", handleUpdate);
            window.addEventListener("devedorStatusUpdated", handleUpdate);
            return () => {
                window.removeEventListener("devedorDocumentoUpdated", handleUpdate);
                window.removeEventListener("devedorStatusUpdated", handleUpdate);
            };
        }
    }, [recordId]);

    // Drag and Drop handlers
    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            setSelectedFile(e.dataTransfer.files[0]);
            setOpenUploadModal(true);
        }
    };

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
            setOpenUploadModal(true);
        }
    };

    const handleUploadSubmit = () => {
        if (!selectedFile) return;
        setUploading(true);
        const formData = new FormData();
        formData.append("file", selectedFile);
        formData.append("tipoDocumentoId", selectedTipo);
        formData.append("observacao", observacao);

        postUpload(`/dossie/upload/${recordId}`, formData)
            .then(() => {
                setOpenUploadModal(false);
                setSelectedFile(null);
                setObservacao("");
                loadDocumentos();
            })
            .catch(err => console.error("Erro no upload", err))
            .finally(() => setUploading(false));
    };

    const handleDownloadDossie = () => {
        getFile(`/dossie/download/${recordId}`)
            .catch(err => alert("Erro ao baixar PDF: " + err.message));
    };

    const getFileIcon = (fileName = "", fileType = "") => {
        const ext = fileName.split('.').pop().toLowerCase();
        if (ext === "pdf") return { icon: "picture_as_pdf", color: "#f44336", label: "PDF" };
        if (["doc", "docx"].includes(ext)) return { icon: "description", color: "#2196f3", label: "DOCX" };
        if (["xls", "xlsx", "csv"].includes(ext)) return { icon: "table_chart", color: "#4caf50", label: "XLSX" };
        if (["jpg", "jpeg", "png", "webp"].includes(ext)) return { icon: "image", color: "#ff9800", label: "IMG" };
        if (["zip", "rar", "7z"].includes(ext)) return { icon: "folder_zip", color: "#9c27b0", label: "ZIP" };
        return { icon: "insert_drive_file", color: "#607d8b", label: "FILE" };
    };

    const filteredDocs = documentos.filter(doc => {
        const matchesSearch = (doc.nomeArquivo || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (doc.observacao || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                              (doc.tipoDocumentoDescricao || "").toLowerCase().includes(searchTerm.toLowerCase());
        
        if (filterType === "TODOS") return matchesSearch;
        return matchesSearch && doc.tipoDocumentoDescricao === filterType;
    });

    return (
        <MDBox pt={1} pb={3}>
            {/* Header section with Action Buttons */}
            <MDBox display="flex" justifyContent="flex-end" alignItems="center" mb={2} gap={1.5}>
                <MDButton 
                    variant="outlined" 
                    color={darkMode ? "white" : "info"} 
                    size="small"
                    startIcon={<Icon>download</Icon>}
                    onClick={() => setSubTab(1)}
                >
                    Baixar Modelos
                </MDButton>
                <MDButton 
                    variant="gradient" 
                    color="info" 
                    size="small"
                    startIcon={<Icon>upload_file</Icon>}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Enviar Documento
                </MDButton>
                <input 
                    type="file" 
                    ref={fileInputRef} 
                    style={{ display: "none" }} 
                    onChange={handleFileSelect} 
                />
            </MDBox>

            {/* Info Banners */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 1.5, background: darkMode ? "#4B4F5C" : "#fafafa", border: "1px solid", borderColor: darkMode ? "#3d4252" : "#e0e0e0" }}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon color="info" fontSize="small">info</Icon>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" display="block">Organize seus documentos</MDTypography>
                                <MDTypography variant="caption" color="text">Tudo centralizado em um só lugar</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 1.5, background: darkMode ? "#4B4F5C" : "#fafafa", border: "1px solid", borderColor: darkMode ? "#3d4252" : "#e0e0e0" }}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon color="success" fontSize="small">article</Icon>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" display="block">Formatos Aceitos</MDTypography>
                                <MDTypography variant="caption" color="text">PDF, DOCX, XLSX, PNG, JPG, ZIP</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Card sx={{ p: 1.5, background: darkMode ? "#4B4F5C" : "#fafafa", border: "1px solid", borderColor: darkMode ? "#3d4252" : "#e0e0e0" }}>
                        <MDBox display="flex" alignItems="center" gap={1}>
                            <Icon color="warning" fontSize="small">cloud_upload</Icon>
                            <MDBox>
                                <MDTypography variant="button" fontWeight="bold" display="block">Tamanho Máximo</MDTypography>
                                <MDTypography variant="caption" color="text">Até 25 MB por arquivo</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
            </Grid>

            {/* Sub-tabs selection */}
            <MDBox sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
                <Tabs value={subTab} onChange={(e, val) => setSubTab(val)}>
                    <Tab label={`Documentos da Ficha (${documentos.length})`} sx={{ fontSize: 13, fontWeight: "bold" }} />
                    <Tab label={`Modelos Disponíveis (${MODELOS_AUTOMATICOS.length})`} sx={{ fontSize: 13, fontWeight: "bold" }} />
                </Tabs>
            </MDBox>

            {subTab === 0 && (
                <>
                    {/* Top Upload Dropzone Full Width */}
                    <MDBox mb={3}>
                        <Card 
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ 
                                p: 3, 
                                textAlign: "center", 
                                cursor: "pointer",
                                border: "2px dashed", 
                                borderColor: dragActive ? "#1A73E8" : (darkMode ? "#555" : "#ccc"),
                                background: dragActive ? (darkMode ? "#4B4F5C" : "#e8f0fe") : (darkMode ? "#4B4F5C" : "#fcfcfc"),
                                transition: "all 0.2s ease"
                            }}
                        >
                            <Icon sx={{ fontSize: "48px!important", color: dragActive ? "#1A73E8" : "#9e9e9e", mb: 1 }}>cloud_upload</Icon>
                            <MDTypography variant="button" fontWeight="bold" display="block">
                                Arraste e solte arquivos aqui
                            </MDTypography>
                            <MDTypography variant="caption" color="info" sx={{ textDecoration: "underline", fontWeight: "medium" }}>
                                ou clique para selecionar do computador
                            </MDTypography>
                            <MDTypography variant="caption" display="block" color="text" mt={1}>
                                Documentos anexados ficam imediatamente disponíveis para a equipe
                            </MDTypography>
                        </Card>
                    </MDBox>

                    {/* Filter and Search Bar */}
                    <Card sx={{ p: 2, mb: 2, background: darkMode ? "#4B4F5C" : "#fafafa" }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={4}>
                                <MDInput
                                    size="small"
                                    placeholder="Buscar por nome ou observação..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Icon fontSize="small">search</Icon>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <MDSelect
                                    name="filterType"
                                    value={filterType}
                                    options={[{ id: "TODOS", nome: "Todos os Tipos" }, ...TIPOS_DOCUMENTO.map(t => ({ id: t.label, nome: t.label }))]}
                                    getOptionValue={o => o.id}
                                    getOptionLabel={o => o.nome}
                                    onChange={(opt) => setFilterType(opt ? opt.id : "TODOS")}
                                    placeholder="Filtrar por tipo"
                                />
                            </Grid>
                            <Grid item xs={12} md={5} display="flex" justifyContent="flex-end" alignItems="center">
                                <MDTypography variant="caption" color="text">
                                    Exibindo {filteredDocs.length} de {documentos.length} documento(s)
                                </MDTypography>
                            </Grid>
                        </Grid>
                    </Card>

                    {/* Documents Table */}
                    <TableContainer component={Card} sx={{ background: darkMode ? "#4B4F5C" : "#fff" }}>
                        <Table size="small">
                            <TableHead sx={{ display: "table-header-group" }}>
                                <TableRow>
                                    <TableCell><MDTypography variant="caption" fontWeight="bold">Nome do Documento</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="bold">Tipo</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="bold">Enviado Por</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="bold">Data de Envio</MDTypography></TableCell>
                                    <TableCell><MDTypography variant="caption" fontWeight="bold">Observação</MDTypography></TableCell>
                                    <TableCell align="center"><MDTypography variant="caption" fontWeight="bold">Ações</MDTypography></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDocs.map((doc) => {
                                    const meta = getFileIcon(doc.nomeArquivo);
                                    return (
                                        <TableRow key={doc.id}>
                                            <TableCell>
                                                <MDBox display="flex" alignItems="center" gap={1}>
                                                    <Icon sx={{ color: meta.color }}>{meta.icon}</Icon>
                                                    <MDTypography variant="caption" fontWeight="medium">
                                                        {doc.nomeArquivo || "Documento sem nome"}
                                                    </MDTypography>
                                                </MDBox>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={doc.tipoDocumentoDescricao || "Outros"} 
                                                    size="small" 
                                                    sx={{ fontSize: "0.7rem", height: 22, backgroundColor: darkMode ? "#3e4454" : "#e0e0e0" }} 
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption" color="text">
                                                    {doc.nomeUsuarioCriacao || "Sistema"}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption" color="text">
                                                    {doc.dataUpload ? new Date(doc.dataUpload).toLocaleString() : "-"}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell>
                                                <MDTypography variant="caption" color="text">
                                                    {doc.observacao || "-"}
                                                </MDTypography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <MDBox display="flex" justifyContent="center" gap={0.5}>
                                                    <Tooltip title="Baixar Arquivo">
                                                        <IconButton 
                                                            size="small" 
                                                            color="info"
                                                            onClick={() => getFile(`/dossie/download-file/${doc.id}`, doc.nomeArquivo)}
                                                        >
                                                            <Icon fontSize="small">download</Icon>
                                                        </IconButton>
                                                    </Tooltip>
                                                </MDBox>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}

                                {filteredDocs.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                            <MDTypography variant="caption" color="text">
                                                Nenhum documento encontrado nesta ficha.
                                            </MDTypography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            {subTab === 1 && (
                <Grid container spacing={2}>
                    {MODELOS_AUTOMATICOS.map((model) => (
                        <Grid item xs={12} sm={6} md={4} key={model.id}>
                            <Card sx={{ p: 2.5, background: darkMode ? "#4B4F5C" : "#fafafa", border: "1px solid", borderColor: darkMode ? "#444" : "#e0e0e0", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                                <MDBox mb={2}>
                                    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                                        <Icon color="info" fontSize="large">description</Icon>
                                        <MDTypography variant="h6" fontWeight="bold">
                                            {model.nome}
                                        </MDTypography>
                                    </MDBox>
                                    <MDTypography variant="caption" color="text">
                                        {model.descricao}
                                    </MDTypography>
                                </MDBox>
                                <MDButton 
                                    variant="contained" 
                                    color="info" 
                                    size="small" 
                                    fullWidth
                                    startIcon={<Icon>download</Icon>}
                                    onClick={() => {
                                        if (model.id === "relacao_debitos") {
                                            handleOpenRelacaoDebitos();
                                        } else if (model.id === "dossie") {
                                            handleDownloadDossie();
                                        } else {
                                            alert(`Download do modelo "${model.nome}" iniciado!`);
                                        }
                                    }}
                                >
                                    Baixar Modelo ({model.tipo.toUpperCase()})
                                </MDButton>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}

            {/* Upload Modal */}
            <Dialog open={openUploadModal} onClose={() => setOpenUploadModal(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Enviar Novo Documento</DialogTitle>
                <DialogContent dividers>
                    <MDBox display="flex" flexDirection="column" gap={2} pt={1}>
                        <MDTypography variant="button" fontWeight="medium">
                            Arquivo Selecionado: <strong>{selectedFile?.name}</strong>
                        </MDTypography>
                        <MDSelect
                            name="tipoDocumento"
                            label="Tipo de Documento"
                            value={selectedTipo}
                            options={TIPOS_DOCUMENTO.map(t => ({ id: t.id, nome: t.label }))}
                            getOptionValue={o => o.id}
                            getOptionLabel={o => o.nome}
                            onChange={(opt) => setSelectedTipo(opt ? opt.id : 2)}
                        />
                        <MDInput
                            label="Observações / Descrição"
                            multiline
                            rows={3}
                            value={observacao}
                            onChange={(e) => setObservacao(e.target.value)}
                            placeholder="Descreva informações relevantes sobre este documento..."
                            fullWidth
                        />
                    </MDBox>
                </DialogContent>
                <DialogActions>
                    <MDButton color={darkMode ? "white" : "secondary"} onClick={() => setOpenUploadModal(false)}>
                        Cancelar
                    </MDButton>
                    <MDButton color="info" variant="contained" onClick={handleUploadSubmit} disabled={uploading}>
                        {uploading ? "Enviando..." : "Confirmar e Enviar"}
                    </MDButton>
                </DialogActions>
            </Dialog>

            {/* Relação dos Débitos Extraction Modal */}
            <Dialog open={relacaoModalOpen} onClose={() => setRelacaoModalOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <MDTypography variant="h6">Relação dos Débitos</MDTypography>
                    <MDBox display="flex" gap={1}>
                        <MDButton 
                            variant="contained" 
                            color="info" 
                            size="small" 
                            startIcon={<Icon>print</Icon>}
                            onClick={handlePrintRelacao}
                        >
                            Imprimir / Salvar PDF
                        </MDButton>
                        <MDButton 
                            variant="outlined" 
                            color={darkMode ? "white" : "secondary"} 
                            size="small" 
                            onClick={() => setRelacaoModalOpen(false)}
                        >
                            Fechar
                        </MDButton>
                    </MDBox>
                </DialogTitle>
                <DialogContent dividers sx={{ p: 3, background: darkMode ? "#4B4F5C" : "#fff" }}>
                    {/* Controls section for selecting simulation date */}
                    <Grid container spacing={2} alignItems="center" mb={3}>
                        <Grid item xs={12} sm={6} md={4}>
                            <MDInput 
                                type="date"
                                label="Data Base da Simulação"
                                value={dataSimulacaoInput}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setDataSimulacaoInput(val);
                                    fetchSimulacaoRelacao(val);
                                }}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    {loadingRelacao ? (
                        <MDTypography variant="body2" align="center" py={4}>Carregando relação dos débitos...</MDTypography>
                    ) : relacaoData ? (
                        <MDBox id="relacao-debitos-print-area">
                            <TableContainer sx={{ border: "1px solid #000", borderRadius: 0, mb: 2, overflowX: "hidden" }}>
                                <Table size="small" sx={{ width: "100%", borderCollapse: "collapse" }}>
                                    <TableHead sx={{ display: "table-header-group" }}>
                                        <TableRow sx={{ background: "#b8860b !important" }}>
                                            <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: "#000 !important", fontWeight: "bold" }}>Documento</TableCell>
                                            <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: "#000 !important", fontWeight: "bold" }}>Cliente</TableCell>
                                            <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: "#000 !important", fontWeight: "bold" }}>Nota fiscal</TableCell>
                                            <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: "#000 !important", fontWeight: "bold" }}>Saldo</TableCell>
                                            <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: "#000 !important", fontWeight: "bold" }}>Vencimento</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {relacaoData.contratos && relacaoData.contratos.length > 0 ? (
                                            relacaoData.contratos.map((c, idx) => (
                                                <TableRow key={idx} sx={{ background: darkMode ? "#4B4F5C" : "#fff" }}>
                                                    {idx === 0 && (
                                                        <>
                                                            <TableCell rowSpan={relacaoData.contratos.length} sx={{ px: 1.5, py: 1, border: "1px solid #000", verticalAlign: "top", fontWeight: 500, color: darkMode ? "#fff" : "#000" }}>
                                                                {relacaoData.documento}
                                                            </TableCell>
                                                            <TableCell rowSpan={relacaoData.contratos.length} sx={{ px: 1.5, py: 1, border: "1px solid #000", verticalAlign: "top", fontWeight: 500, color: darkMode ? "#fff" : "#000" }}>
                                                                {relacaoData.cliente}
                                                            </TableCell>
                                                        </>
                                                    )}
                                                    <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: darkMode ? "#fff" : "#000" }}>{c.notaFiscal}</TableCell>
                                                    <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: darkMode ? "#fff" : "#000" }}>{toCurrency(c.saldo)}</TableCell>
                                                    <TableCell sx={{ px: 1.5, py: 1, border: "1px solid #000", color: darkMode ? "#fff" : "#000" }}>{c.vencimento}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center" sx={{ border: "1px solid #000" }}>Nenhum contrato localizado</TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </MDBox>
                    ) : null}
                </DialogContent>
            </Dialog>
        </MDBox>
    );
}
